import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { businessReportsApi, type HospitalPnlLine } from '@/api/businessReports';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

function formatPnlDisplay(
  cellFormat: HospitalPnlLine['format'],
  value: number
): string {
  if (cellFormat === 'percent') {
    return `${(value * 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`;
  }
  if (cellFormat === 'currency') {
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  }
  return value.toLocaleString('en-IN');
}

function formatPnlEditValue(cellFormat: HospitalPnlLine['format'], value: number): string {
  if (cellFormat === 'percent') return String(Number((value * 100).toFixed(4)));
  if (cellFormat === 'currency') return String(Math.round(value));
  return String(value);
}

function parsePnlEditInput(cellFormat: HospitalPnlLine['format'], raw: string): number | null {
  const cleaned = raw.replace(/[₹,\s%]/g, '').trim();
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  if (cellFormat === 'percent') return n / 100;
  return n;
}

type PnlEditableCellProps = {
  cell?: HospitalPnlLine;
  rowFormat: HospitalPnlLine['format'];
  periodKey: string;
  periodMode: 'MTD' | 'YTD';
  hospitalId?: string;
  pnlQueryKey: unknown[];
  editable: boolean;
};

export function PnlEditableCell({
  cell,
  rowFormat,
  periodKey,
  periodMode,
  hospitalId,
  pnlQueryKey,
  editable,
}: PnlEditableCellProps) {
  const queryClient = useQueryClient();
  const fmt = cell?.format ?? rowFormat;
  const value = cell?.value ?? 0;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const saveMutation = useMutation({
    mutationFn: (nextValue: number) =>
      businessReportsApi.hospitalPnlOverrideUpsert({
        lineId: cell?.id ?? '',
        value: nextValue,
        periodKey,
        periodMode,
        hospitalId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pnlQueryKey });
      setEditing(false);
      toast.success('P&L value updated');
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save';
      toast.error(msg);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () =>
      businessReportsApi.hospitalPnlOverrideDelete({
        lineId: cell?.id ?? '',
        periodKey,
        periodMode,
        hospitalId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pnlQueryKey });
      toast.success('Reverted to system value');
    },
    onError: () => toast.error('Failed to reset'),
  });

  const startEdit = () => {
    if (!editable || !cell) return;
    setDraft(formatPnlEditValue(fmt, value));
    setEditing(true);
  };

  const commitEdit = () => {
    if (!cell) return;
    const parsed = parsePnlEditInput(fmt, draft);
    if (parsed == null) {
      toast.error('Enter a valid number');
      return;
    }
    saveMutation.mutate(parsed);
  };

  const tooltip =
    cell?.isEstimated && cell.computedValue != null
      ? `Estimated from prior month (actual: ${formatPnlDisplay(fmt, cell.computedValue)})`
      : cell?.isOverridden && cell.computedValue != null
      ? `System: ${formatPnlDisplay(fmt, cell.computedValue)} (double-click to edit, right-click to reset)`
      : editable
        ? 'Double-click to edit'
        : undefined;

  if (editing) {
    return (
      <Input
        ref={inputRef}
        className="h-8 w-28 ml-auto text-right text-sm"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commitEdit();
          if (e.key === 'Escape') setEditing(false);
        }}
        disabled={saveMutation.isPending}
      />
    );
  }

  return (
    <button
      type="button"
      title={tooltip}
      className={cn(
        'w-full text-right whitespace-nowrap rounded px-1 py-0.5',
        editable && 'cursor-pointer hover:bg-amber-100/80',
        cell?.isOverridden && 'bg-amber-50 font-medium text-amber-950 ring-1 ring-amber-200/80',
        cell?.isEstimated &&
          !cell?.isOverridden &&
          'bg-sky-50 text-sky-950 ring-1 ring-sky-200/80 italic'
      )}
      onDoubleClick={startEdit}
      onContextMenu={(e) => {
        if (!editable || !cell?.isOverridden) return;
        e.preventDefault();
        resetMutation.mutate();
      }}
    >
      {formatPnlDisplay(fmt, value)}
      {cell?.isEstimated ? (
        <span className="ml-1 text-[10px] font-normal uppercase tracking-wide text-sky-700 not-italic">
          est.
        </span>
      ) : null}
    </button>
  );
}
